import {
  ReactFlowProvider,
  ReactFlow,
  Background,
  BackgroundVariant,
  type NodeTypes,
  type EdgeTypes,
  useReactFlow,
  useStore,
  getNodesBounds,
  getViewportForBounds,
} from "@xyflow/react";
import "@xyflow/react/dist/style.css";
import { useRef, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  Download,
  FileText,
  Loader2,
  MessageSquareText,
  X,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { domToPng } from "modern-screenshot";
import jsPDF from "jspdf";
import { ZoomSlider } from "@/components/zoom-slider";
import { useTheme } from "@/hooks/useTheme";
import { DiagramDirectionProvider } from "@/components/silae/validation-diagram/DiagramDirectionContext";
import { SourceNode } from "@/components/silae/validation-diagram/nodes/SourceNode";
import { ExclusionsNode } from "@/components/silae/validation-diagram/nodes/ExclusionsNode";
import { ConditionsNode } from "@/components/silae/validation-diagram/nodes/ConditionsNode";
import { BonusConditionsNode } from "@/components/silae/validation-diagram/nodes/BonusConditionsNode";
import { BonusNode } from "@/components/silae/validation-diagram/nodes/BonusNode";
import { NoBonusNode } from "@/components/silae/validation-diagram/nodes/NoBonusNode";
import { BranchGroupNode } from "@/components/silae/validation-diagram/nodes/BranchGroupNode";
import { BranchEdge } from "@/components/silae/validation-diagram/edges/BranchEdge";
import { buildGraph } from "@/components/silae/validation-diagram/utils/buildGraph";
import { useElkLayout } from "@/components/silae/validation-diagram/hooks/useElkLayout";
import type { SilaeFormData } from "@/types/silae";

const nodeTypes: NodeTypes = {
  source: SourceNode,
  exclusions: ExclusionsNode,
  conditions: ConditionsNode,
  bonusConditions: BonusConditionsNode,
  bonus: BonusNode,
  noBonus: NoBonusNode,
  branchGroup: BranchGroupNode,
};

const edgeTypes: EdgeTypes = { branch: BranchEdge };

interface AiValidationDiagramProps {
  formData: SilaeFormData;
}

export function AiValidationDiagram({ formData }: AiValidationDiagramProps) {
  return (
    <ReactFlowProvider>
      <AiValidationDiagramInner formData={formData} />
    </ReactFlowProvider>
  );
}

function AiValidationDiagramInner({ formData }: AiValidationDiagramProps) {
  const { t } = useTranslation();
  const { isDark } = useTheme();
  const [isCapturing, setIsCapturing] = useState(false);
  const [isCommentOpen, setIsCommentOpen] = useState(false);
  const [comment, setComment] = useState("");
  // Ref scoped to THIS diagram instance — fixes capture always picking the first
  // .react-flow element in the DOM when multiple diagrams are rendered.
  const containerRef = useRef<HTMLDivElement>(null);

  const built = useMemo(() => buildGraph(formData), [formData]);
  const { nodes, edges, isLayouting } = useElkLayout(built, "DOWN");
  useReactFlow();
  const nodeLookup = useStore((s) => s.nodeLookup);

  const exportFileName =
    (formData.ifBranch?.bonuses?.[0]?.libelle?.trim() || "diagramme")
      .replace(/[^a-z0-9\-_À-ɏ ]/gi, "")
      .trim() || "diagramme";

  function waitForPaint(): Promise<void> {
    return new Promise((resolve) =>
      requestAnimationFrame(() => requestAnimationFrame(() => resolve())),
    );
  }

  function captureDiagram(
    pixelRatio: number,
    maxW?: number,
    maxH?: number,
  ): Promise<string> {
    const scope = containerRef.current ?? document;
    const rfRoot = scope.querySelector<HTMLElement>(".react-flow");
    const viewport = scope.querySelector<HTMLElement>(".react-flow__viewport");
    if (!rfRoot || !viewport)
      return Promise.reject(new Error(t("aiAgent.diagram.generating")));

    const PADDING = 40;
    const bounds = getNodesBounds(nodes, { nodeLookup });
    let imgW = Math.round(bounds.width + PADDING * 2);
    let imgH = Math.round(bounds.height + PADDING * 2);
    if (maxW && maxH) {
      const scale = Math.min(maxW / imgW, maxH / imgH, 1);
      imgW = Math.round(imgW * scale);
      imgH = Math.round(imgH * scale);
    }

    const { x, y, zoom } = getViewportForBounds(
      bounds,
      imgW,
      imgH,
      0.05,
      4,
      PADDING / Math.max(bounds.width, bounds.height),
    );

    const styleEl = document.createElement("style");
    styleEl.textContent = `.react-flow__viewport[data-capture="1"] { transform: translate(${x}px, ${y}px) scale(${zoom}) !important; transform-origin: top left !important; }`;
    document.head.appendChild(styleEl);
    viewport.setAttribute("data-capture", "1");

    const cleanup = () => {
      viewport.removeAttribute("data-capture");
      styleEl.parentNode?.removeChild(styleEl);
    };

    const exclude = (el: Node) => {
      if (!(el instanceof Element)) return true;
      const cls = el.classList;
      return !(
        cls.contains("react-flow__minimap") ||
        cls.contains("react-flow__controls") ||
        cls.contains("react-flow__background") ||
        cls.contains("react-flow__panel") ||
        cls.contains("react-flow__attribution")
      );
    };

    return document.fonts.ready
      .then(() =>
        domToPng(rfRoot, {
          backgroundColor: isDark ? "#09090b" : "#ffffff",
          width: imgW,
          height: imgH,
          style: { width: `${imgW}px`, height: `${imgH}px` },
          scale: pixelRatio,
          filter: exclude,
          font: {},
        }),
      )
      .then(
        (url) => {
          cleanup();
          return url;
        },
        (err) => {
          cleanup();
          throw err;
        },
      );
  }

  function handleDownloadPng() {
    setIsCapturing(true);
    waitForPaint()
      .then(() => captureDiagram(3))
      .then((url) => {
        const a = document.createElement("a");
        a.href = url;
        a.download = `${exportFileName}.png`;
        a.click();
      })
      .catch(console.error)
      .finally(() => setIsCapturing(false));
  }

  function handleDownloadPdf() {
    setIsCapturing(true);
    waitForPaint()
      .then(() => captureDiagram(3, 2338, 1654))
      .then(
        (dataUrl) =>
          new Promise<void>((resolve, reject) => {
            const img = new Image();
            img.onload = () => {
              const canvas = document.createElement("canvas");
              canvas.width = img.naturalWidth;
              canvas.height = img.naturalHeight;
              const ctx = canvas.getContext("2d")!;
              ctx.fillStyle = isDark ? "#09090b" : "#ffffff";
              ctx.fillRect(0, 0, canvas.width, canvas.height);
              ctx.drawImage(img, 0, 0);
              const jpegUrl = canvas.toDataURL("image/jpeg", 0.92);

              const pdf = new jsPDF({
                orientation: "landscape",
                unit: "mm",
                format: "a4",
              });

              if (comment.trim()) {
                pdf.setFontSize(11);
                pdf.setTextColor(isDark ? 230 : 30);
                pdf.text(t("aiAgent.diagram.commentLabel"), 14, 20);
                pdf.setFontSize(10);
                pdf.text(pdf.splitTextToSize(comment, 267), 14, 30);
                pdf.addPage("a4", "landscape");
              }

              const pageW = pdf.internal.pageSize.getWidth();
              const pageH = pdf.internal.pageSize.getHeight();
              const margin = 10;
              const scale = Math.min(
                (pageW - margin * 2) / img.naturalWidth,
                (pageH - margin * 2) / img.naturalHeight,
              );
              const drawW = img.naturalWidth * scale;
              const drawH = img.naturalHeight * scale;
              pdf.addImage(
                jpegUrl,
                "JPEG",
                margin + (pageW - margin * 2 - drawW) / 2,
                margin + (pageH - margin * 2 - drawH) / 2,
                drawW,
                drawH,
              );
              pdf.save(`${exportFileName}.pdf`);
              resolve();
            };
            img.onerror = () => reject(new Error("image load failed"));
            img.src = dataUrl;
          }),
      )
      .catch(console.error)
      .finally(() => setIsCapturing(false));
  }

  return (
    <DiagramDirectionProvider value="DOWN">
      {/* Wrapper global */}
      <div className="w-full rounded-xl border border-border overflow-hidden mt-3 shadow-sm">
        {/* Toolbar */}
        <div className="flex items-center justify-between px-3 py-2 bg-primary/5 border-b border-primary/10">
          <div className="flex items-center gap-1.5">
            <span className="text-[11px] font-regular text-primary/70 tracking-widest uppercase">
              {t("aiAgent.diagram.title")}
            </span>
          </div>
          <div className="flex items-center gap-0.5">
            <Button
              variant="ghost"
              size="sm"
              onClick={handleDownloadPng}
              className="h-7 gap-1.5 px-2.5 text-xs text-muted-foreground hover:text-foreground hover:bg-background/80"
              title={t("aiAgent.diagram.downloadPng")}
            >
              <Download className="h-3 w-3" /> PNG
            </Button>
            <div className="w-px h-4 bg-border mx-0.5" />
            <Button
              variant="ghost"
              size="sm"
              onClick={handleDownloadPdf}
              className="h-7 gap-1.5 px-2.5 text-xs text-muted-foreground hover:text-foreground hover:bg-background/80"
              title={t("aiAgent.diagram.downloadPdf")}
            >
              <FileText className="h-3 w-3" /> PDF
            </Button>
            <div className="w-px h-4 bg-border mx-0.5" />
            <Button
              variant="ghost"
              size="sm"
              onClick={() => setIsCommentOpen((v) => !v)}
              aria-pressed={isCommentOpen}
              className={[
                "h-7 gap-1.5 px-2.5 text-xs transition-colors",
                isCommentOpen || comment.length > 0
                  ? "bg-violet-100 text-violet-700 dark:bg-violet-900/40 dark:text-violet-300 border border-primary/40"
                  : "text-muted-foreground hover:text-foreground hover:bg-background/80",
              ].join(" ")}
            >
              <MessageSquareText className="h-3 w-3" /> {t("aiAgent.diagram.commentLabel")}
            </Button>
          </div>
        </div>

        {/* Panneau commentaire */}
        {isCommentOpen && (
          <div className="px-3 py-2.5 border-b border-border bg-background/60">
            <div className="flex items-center justify-between mb-1.5">
              <span className="text-xs font-medium text-muted-foreground">
                {t("aiAgent.diagram.commentHint")}
              </span>
              <div className="flex items-center gap-2">
                <span className="text-[11px] text-muted-foreground tabular-nums">
                  {comment.length}/1500
                </span>
                <button
                  onClick={() => setIsCommentOpen(false)}
                  className="p-0.5 rounded hover:bg-muted transition-colors text-muted-foreground cursor-pointer"
                  aria-label={t("aiAgent.diagram.close")}
                >
                  <X className="h-3.5 w-3.5" />
                </button>
              </div>
            </div>
            <textarea
              value={comment}
              onChange={(e) => setComment(e.target.value.slice(0, 1500))}
              rows={3}
              placeholder={t("aiAgent.diagram.commentPlaceholder")}
              autoFocus
              className="w-full resize-none rounded-lg border border-border bg-background px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-violet-400/40 placeholder:text-muted-foreground"
            />
          </div>
        )}

        {/* Canvas ReactFlow — ref scoped to this instance for correct capture */}
        <div
          ref={containerRef}
          className="relative rounded-b-xl overflow-hidden"
          style={{ height: 420 }}
        >
          {isLayouting && nodes.length === 0 && (
            <div className="absolute inset-0 flex items-center justify-center text-xs text-muted-foreground z-10">
              {t("aiAgent.diagram.generating")}
            </div>
          )}
          {isCapturing && (
            <div className="absolute inset-0 z-50 flex items-center justify-center gap-2 bg-white dark:bg-zinc-950 rounded-b-xl">
              <Loader2 className="h-5 w-5 animate-spin text-violet-500" />
              <span className="text-sm font-medium text-muted-foreground">
                {t("aiAgent.diagram.generatingPdf")}
              </span>
            </div>
          )}
          <ReactFlow
            colorMode={isDark ? "dark" : "light"}
            nodes={nodes}
            edges={edges}
            nodeTypes={nodeTypes}
            edgeTypes={edgeTypes}
            fitView
            fitViewOptions={{ padding: 0.25 }}
            minZoom={0.15}
            maxZoom={2}
            proOptions={{ hideAttribution: true }}
            nodesDraggable={false}
            nodesConnectable={false}
            elementsSelectable={false}
          >
            <Background variant={BackgroundVariant.Dots} gap={16} size={1} />
            <ZoomSlider position="bottom-left" collapsible />
          </ReactFlow>
        </div>
      </div>
    </DiagramDirectionProvider>
  );
}
